import { useRef, useState } from 'react';

import { ONE, ZERO } from '@/constants';
import { useCurrentUser, useSurvey, useSurveyTemplate, useView } from '@/hooks';
import { SurveyResponseStatus, SurveyStatus } from '@/types';
import { Paper } from '@/ui';

import CoverBackground from './CoverBackground';
import ManageRecipients from './ManageRecipients';
import Navigation from './Navigation';
import Slides from './Slides';
import Start from './Start';
import {
  useViewerKeyboardNavigation,
  type ViewerKeyboardState,
} from './useViewerKeyboardNavigation';

import type { SummarySlide, SurveyAggregated, SurveyResult } from '@/types';
import type { FC } from 'react';

interface ViewerProps {
  isExample?: boolean;
}

const Viewer: FC<ViewerProps> = ({ isExample = false }) => {
  const [page, setPage] = useState(-ONE);
  const { survey, isSurveyLoading } = useSurvey();
  const { surveyTemplate, isSurveyTemplateLoading } = useSurveyTemplate();
  const { user: currentUser } = useCurrentUser();
  const { selectedUser, selectedUserId } = useView();

  // Reset each render; only set once the viewer is actually showing slides.
  const keyboardState = useRef<ViewerKeyboardState | null>(null);
  keyboardState.current = null;
  useViewerKeyboardNavigation(keyboardState);

  if (isSurveyLoading || isSurveyTemplateLoading) return null;

  const responses = isExample
    ? surveyTemplate.exampleSurvey?.responses
    : survey.responses;

  // Prefer aggregated data when available (member view after SHARED)
  const aggregated: SurveyAggregated | undefined = isExample
    ? surveyTemplate.exampleSurvey?.aggregated
    : survey.aggregated;

  const results: SurveyResult[] = (responses ?? [])
    .filter((res) => res.results !== undefined)
    .filter((res) => res.status === SurveyResponseStatus.COMPLETED)
    .filter((res) => Boolean(res.results))
    .sort((a, b) => {
      const toTime = (value?: Date | string | null) =>
        value ? new Date(value).valueOf() : 0;
      return (
        toTime(b.respondedAt ?? b.updatedAt ?? b.createdAt) -
        toTime(a.respondedAt ?? a.updatedAt ?? a.createdAt)
      );
    })
    .map(({ results: res }) => res as SurveyResult);

  const slideList: SummarySlide[] = [];
  const questions = Array.isArray(surveyTemplate.surveyQuestions)
    ? surveyTemplate.surveyQuestions
    : [];
  questions.forEach((question) => {
    if (question.options?.length) {
      slideList.push(question);
    } else {
      // Free-text questions: prefer aggregated text arrays when present
      const questionName = question.name ?? '';
      if (aggregated && questionName === 'Strengths') {
        (aggregated.strengthsText ?? []).forEach((t) =>
          slideList.push({ ...question, text: t }),
        );
      } else if (aggregated && questionName === 'Growth Opportunities') {
        (aggregated.growthText ?? []).forEach((t) =>
          slideList.push({ ...question, text: t }),
        );
      } else {
        results.forEach((result) => {
          const { text } = result[question.name ?? ''] ?? {};
          if (text) {
            slideList.push({ ...question, text });
          }
        });
      }
    }
  });

  const pages = slideList.length - ONE;
  const previousPage = () => setPage(Math.max(page - ONE, -ONE));
  const nextPage = () => setPage(Math.min(page + ONE, pages));

  // Check if current user is viewing their own profile
  const isViewingOwnProfile =
    !selectedUserId || currentUser.id === selectedUser.id;

  const shouldShowManageRecipients =
    !isExample &&
    isViewingOwnProfile &&
    [SurveyStatus.IN_PROGRESS, SurveyStatus.COACH_REVIEWING].includes(
      survey.status,
    );

  if (shouldShowManageRecipients) {
    return (
      <Paper className="mb-20 flex h-fit min-h-158 flex-col rounded-3xl border border-cement-600 bg-white p-6 pb-11 lg:p-16">
        <ManageRecipients />
      </Paper>
    );
  }

  keyboardState.current = {
    page,
    pages,
    setPage,
    slideNames: slideList.map((slide) => slide.name),
  };

  if (page < ZERO) {
    return (
      <Paper className="mb-20 h-158 rounded-3xl border border-cement-300 p-0">
        <CoverBackground className="size-full rounded-3xl p-6 pb-11 lg:p-16">
          <Start
            isExample={isExample}
            name={surveyTemplate.name}
            next={nextPage}
            results={aggregated?.totalResponses ?? results.length}
            {...survey}
          />
        </CoverBackground>
      </Paper>
    );
  }

  return (
    <Paper className="mb-20 flex h-fit min-h-158 flex-col rounded-3xl border border-cement-600 bg-white p-6 pb-11 lg:p-16">
      <Slides
        aggregated={aggregated}
        page={page}
        results={results}
        slides={slideList}
      />
      <Navigation
        back={previousPage}
        next={nextPage}
        page={page}
        pages={pages}
      />
    </Paper>
  );
};

export default Viewer;
